/**
 * ReceiveHistoryTable — BLU-3xx Phase 47.01.2-B-05
 *
 * Receipts list for the Receive History page. Renders a shadcn `Table` with
 * loading skeleton, empty state, and per-row Reverse trigger. Owns the
 * AlertDialog for the two-click reverse flow and the mutation wiring back to
 * `inventory.reverseReceipt` (race-safe idempotent backend per B-02).
 *
 * Cross-org awareness:
 *   - When `showOwnerColumn` is true (unfiltered + multi-org rows), each
 *     row gets a left-edge ribbon for client-owned rows + an owner cell.
 *     Mirrors B-03's `InventoryTableList.tsx` pattern.
 *   - When narrowed to a single client (server-side `ownerOrgId` filter on
 *     the parent query), the owner column is suppressed — the page-level
 *     context indicator already names the client. Per B-03 SUMMARY: avoids
 *     redundant ribbon + banner.
 *
 * Mobile note: the table wraps in `overflow-x-auto`. The Reverse button
 * stays compact (`h-8`) so it remains a single tap target on narrow
 * screens.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { TooltipProvider } from '@/components/ui/tooltip';
import { trpc } from '@/lib/trpc';

import { ReverseReceiptDialog } from './ReverseReceiptDialog';
import {
  ReceiveHistoryRow,
  type ReceiveHistoryReceipt,
} from './ReceiveHistoryRow';
import { hexSlice } from '../utils/partnerFallback';

interface ReceiveHistoryTableProps {
  receipts: ReceiveHistoryReceipt[];
  isLoading: boolean;
  showOwnerColumn: boolean;
  ownOrgId?: string;
  orgIdToName: Map<string, string>;
  /** BLU-3xx Phase 47.01.2-B-fix3.5 (BUG 4): optimistic-remove callback.
   *  Parent owns the local `pages` accumulator; on reverse success we
   *  call this so the row disappears instantly, then a backend
   *  invalidation re-runs the query as a backstop. */
  onReverseSuccess?: (receiptId: string) => void;
}

export function ReceiveHistoryTable({
  receipts,
  isLoading,
  showOwnerColumn,
  ownOrgId,
  orgIdToName,
  onReverseSuccess,
}: ReceiveHistoryTableProps) {
  const utils = trpc.useUtils();
  const reverseReceipt = trpc.inventory.reverseReceipt.useMutation();
  const { t } = useTranslation('inventory');

  const [pendingReceipt, setPendingReceipt] =
    useState<ReceiveHistoryReceipt | null>(null);

  // True iff actor != owner for ANY visible row — when every row's actor is
  // its own owner (single-tenant queue), the column adds visual noise.
  const showActorColumn = receipts.some(
    (r) =>
      r.actorOrganizationId &&
      r.actorOrganizationId !== r.ownerOrganizationId,
  );

  const orgName = (orgId: string | null): string | null => {
    if (!orgId) return null;
    // BLU-3xx Phase 47.01.2-B-fix2 NEW-FH2: shared `partnerFallbackHex`
    // key + `hexSlice` util keeps the unresolved label consistent
    // across all cross-org surfaces.
    return orgIdToName.get(orgId) ?? t('crossOrg.partnerFallbackHex', { hex: hexSlice(orgId) });
  };

  const handleConfirm = async () => {
    if (!pendingReceipt) return;
    const idAtClick = pendingReceipt.id;
    try {
      await reverseReceipt.mutateAsync({ transactionId: idAtClick });
      // Backend is idempotent — the second arrival short-circuits and returns
      // the existing reversal id, so this branch always represents success.
      toast.success(t('receiveHistory.reversedToast'));
      // BLU-3xx Phase 47.01.2-B-fix3.5 (BUG 4): optimistically remove the
      // row from the parent's local pages accumulator so the user sees
      // instant feedback. The DAO filters reversed receipts out of
      // listRecentReceipts already, so the next refetch confirms the
      // removal — the optimistic step just closes the visual gap that
      // previously required a manual page refresh.
      onReverseSuccess?.(idAtClick);
      // Invalidate the entire inventory namespace: listRecentReceipts +
      // getProducts (stock counts changed). Dispose-and-recompute is cheap
      // here because the page is a single query.
      await utils.inventory.invalidate();
      setPendingReceipt(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      // The backend differentiates `forbidden` (junction revoked / not in
      // accessibleOrgIds) and `bad_request` (non-receipt source). The toast
      // surfaces the message verbatim so the operator can act.
      toast.error(t('receiveHistory.reverseFailedToast'), {
        description: message,
      });
      // Leave the dialog open so the operator can read the failure context;
      // they can dismiss with "Keep receipt" once they've seen the toast.
    }
  };

  // Empty state. Distinct from "filtered to zero" — the parent decides which
  // empty message to show by short-circuiting around this component when
  // `receipts.length === 0` AND a filter is active. Here we render a neutral
  // table-empty cell that matches the rest of the inventory app.
  if (!isLoading && receipts.length === 0) {
    const colSpan =
      3 + (showOwnerColumn ? 1 : 0) + (showActorColumn ? 1 : 0) + 1;
    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[140px]">{t('receiveHistory.col.date')}</TableHead>
              <TableHead>{t('receiveHistory.col.sku')}</TableHead>
              <TableHead className="text-right w-[100px]">{t('receiveHistory.col.quantity')}</TableHead>
              {showOwnerColumn && (
                <TableHead className="w-[160px]">{t('receiveHistory.col.owner')}</TableHead>
              )}
              {showActorColumn && (
                <TableHead className="w-[160px]">{t('receiveHistory.col.actor')}</TableHead>
              )}
              <TableHead className="text-right w-[140px]">
                <span className="sr-only">{t('receiveHistory.col.actions')}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell colSpan={colSpan} className="h-32 text-center">
                <div className="flex flex-col items-center gap-1.5 text-muted-foreground">
                  <span className="text-sm font-medium text-foreground">
                    {t('receiveHistory.empty')}
                  </span>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[140px]">{t('receiveHistory.col.date')}</TableHead>
              <TableHead>{t('receiveHistory.col.sku')}</TableHead>
              <TableHead className="text-right w-[100px]">{t('receiveHistory.col.quantity')}</TableHead>
              {showOwnerColumn && (
                <TableHead className="w-[160px]">{t('receiveHistory.col.owner')}</TableHead>
              )}
              {showActorColumn && (
                <TableHead className="w-[160px]">{t('receiveHistory.col.actor')}</TableHead>
              )}
              <TableHead className="text-right w-[140px]">
                <span className="sr-only">{t('receiveHistory.col.actions')}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? Array.from({ length: 6 }).map((_, idx) => (
                  <TableRow key={`skeleton-${idx}`}>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-40" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Skeleton className="h-4 w-12 ml-auto" />
                    </TableCell>
                    {showOwnerColumn && (
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                    )}
                    {showActorColumn && (
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                    )}
                    <TableCell className="text-right">
                      <Skeleton className="h-8 w-20 ml-auto" />
                    </TableCell>
                  </TableRow>
                ))
              : receipts.map((receipt) => (
                  <ReceiveHistoryRow
                    key={receipt.id}
                    receipt={receipt}
                    showOwnerColumn={showOwnerColumn}
                    showActorColumn={showActorColumn}
                    ownOrgId={ownOrgId}
                    ownerName={
                      orgName(receipt.ownerOrganizationId) ?? '-'
                    }
                    actorName={orgName(receipt.actorOrganizationId)}
                    onReverseClick={setPendingReceipt}
                    isPending={
                      reverseReceipt.isPending &&
                      pendingReceipt?.id === receipt.id
                    }
                  />
                ))}
          </TableBody>
        </Table>
      </div>

      <ReverseReceiptDialog
        receipt={pendingReceipt}
        ownerOrgName={
          pendingReceipt ? orgName(pendingReceipt.ownerOrganizationId) : null
        }
        actorOrgName={
          pendingReceipt ? orgName(pendingReceipt.actorOrganizationId) : null
        }
        isPending={reverseReceipt.isPending}
        onClose={() => setPendingReceipt(null)}
        onConfirm={handleConfirm}
      />
    </TooltipProvider>
  );
}
